import { useCallback, useState } from 'react';
import { storedString } from '../utils/tesoreriaFiltrosStorage';

const FECHA_DESDE_KEY = 'ventas.fechaDesde';
const FECHA_HASTA_KEY = 'ventas.fechaHasta';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function defaultMesActualRango(): { fechaDesde: string; fechaHasta: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const formatLocal = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    fechaDesde: formatLocal(first),
    fechaHasta: formatLocal(last),
  };
}

function readStoredDate(key: string, fallback: string): string {
  try {
    const raw = localStorage.getItem(key);
    if (raw && ISO_DATE.test(raw)) return raw;
  } catch {}
  return fallback;
}

export function readGlobalFechaDesde(): string {
  return readStoredDate(FECHA_DESDE_KEY, defaultMesActualRango().fechaDesde);
}

export function readGlobalFechaHasta(): string {
  return readStoredDate(FECHA_HASTA_KEY, defaultMesActualRango().fechaHasta);
}

export function persistGlobalFechaDesde(fechaDesde: string): void {
  if (!ISO_DATE.test(fechaDesde)) return;
  try {
    localStorage.setItem(FECHA_DESDE_KEY, fechaDesde);
  } catch {}
}

export function persistGlobalFechaHasta(fechaHasta: string): void {
  if (!ISO_DATE.test(fechaHasta)) return;
  try {
    localStorage.setItem(FECHA_HASTA_KEY, fechaHasta);
  } catch {}
}

export function clearGlobalFechaDesde(): void {
  try {
    localStorage.removeItem(FECHA_DESDE_KEY);
  } catch {}
}

export function clearGlobalFechaHasta(): void {
  try {
    localStorage.removeItem(FECHA_HASTA_KEY);
  } catch {}
}

/** Valor inicial: storage de página si existe; si no, fechas globales. */
export function resolveInitialFechaDesde(
  stored: object = {},
  key = 'fechaDesde',
  urlOverride?: string | null,
): string {
  if (urlOverride && ISO_DATE.test(urlOverride)) return urlOverride;
  const record = stored as Partial<Record<string, unknown>>;
  if (key in record) {
    const value = storedString(record, key, '');
    if (value && ISO_DATE.test(value)) return value;
    return defaultMesActualRango().fechaDesde;
  }
  return readGlobalFechaDesde();
}

export function resolveInitialFechaHasta(
  stored: object = {},
  key = 'fechaHasta',
  urlOverride?: string | null,
): string {
  if (urlOverride && ISO_DATE.test(urlOverride)) return urlOverride;
  const record = stored as Partial<Record<string, unknown>>;
  if (key in record) {
    const value = storedString(record, key, '');
    if (value && ISO_DATE.test(value)) return value;
    return defaultMesActualRango().fechaHasta;
  }
  return readGlobalFechaHasta();
}

export interface FechasVentaFiltroOptions {
  initialFechaDesde?: string;
  initialFechaHasta?: string;
  urlFechaDesde?: string | null;
  urlFechaHasta?: string | null;
}

export function useFechasVentaFiltro(options: FechasVentaFiltroOptions = {}) {
  const { initialFechaDesde, initialFechaHasta, urlFechaDesde, urlFechaHasta } = options;

  const [fechaDesde, setFechaDesdeState] = useState<string>(() => {
    if (urlFechaDesde && ISO_DATE.test(urlFechaDesde)) return urlFechaDesde;
    if (initialFechaDesde !== undefined) {
      return initialFechaDesde && ISO_DATE.test(initialFechaDesde)
        ? initialFechaDesde
        : defaultMesActualRango().fechaDesde;
    }
    return readGlobalFechaDesde();
  });

  const [fechaHasta, setFechaHastaState] = useState<string>(() => {
    if (urlFechaHasta && ISO_DATE.test(urlFechaHasta)) return urlFechaHasta;
    if (initialFechaHasta !== undefined) {
      return initialFechaHasta && ISO_DATE.test(initialFechaHasta)
        ? initialFechaHasta
        : defaultMesActualRango().fechaHasta;
    }
    return readGlobalFechaHasta();
  });

  const setFechaDesde = useCallback((value: string) => {
    setFechaDesdeState(value);
    if (value && ISO_DATE.test(value)) {
      persistGlobalFechaDesde(value);
    } else {
      clearGlobalFechaDesde();
    }
  }, []);

  const setFechaHasta = useCallback((value: string) => {
    setFechaHastaState(value);
    if (value && ISO_DATE.test(value)) {
      persistGlobalFechaHasta(value);
    } else {
      clearGlobalFechaHasta();
    }
  }, []);

  return { fechaDesde, fechaHasta, setFechaDesde, setFechaHasta };
}
